import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Inbox, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InstallationDeleteConfirmDialog } from '@/features/installations/components/installation-delete-confirm-dialog'
import {
  defaultSourceGroupCreateFormState,
  defaultSourceGroupFormState,
  SourceGroupFormFields,
  sourceGroupFormStateFromDto,
  type SourceGroupCreateFormState,
  type SourceGroupFormState,
} from '@/features/installations/components/source-group-form-fields'
import {
  filterSourceGroupEmissionReportsForCnCode,
  formatSourceGroupSupplierInstallationLabel,
  inferSourceGroupEmissionReportId,
  isSourceGroupCreateFormValid,
  isSourceGroupFormValid,
  toSourceGroupCreatePayload,
  toSourceGroupPatchPayload,
  type SourceGroupDto,
} from '@/features/installations/api/source-group-model'
import { useSupplierInstallationEmissionReportsQuery } from '@/features/suppliers/hooks/use-supplier-installation-emission-reports'
import { useInstallationDeleteConfirm } from '@/features/installations/hooks/use-installation-delete-confirm'
import { useInstallationPrecursorCnCodesQuery } from '@/features/installations/hooks/useInstallationConfiguration'
import {
  useInstallationSourceGroupMutations,
  useInstallationSourceGroupsQuery,
} from '@/features/installations/hooks/useInstallationSourceGroups'
import { useOrganizationSupplierInstallationsQuery } from '@/features/suppliers/hooks/use-supplier-installations'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { DialogPortalContainer } from '@/shared/lib/dialog-portal-container'
import { preventDialogDismissForCombobox } from '@/shared/lib/dialog-combobox'

type EmissionReportsQueryState = {
  isPending: boolean
  isFetching: boolean
}

function getSourceGroupEmissionReportsState(
  type: SourceGroupFormState['type'],
  supplierInstallationId: string,
  supplierOrganizationId: string | undefined,
  query: EmissionReportsQueryState
) {
  const queryEnabled =
    type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    supplierInstallationId.trim().length > 0 &&
    Boolean(supplierOrganizationId)

  const awaitingEmissionReports =
    queryEnabled && (query.isPending || query.isFetching)

  return {
    awaitingEmissionReports,
    emissionReportsLoaded: queryEnabled && !awaitingEmissionReports,
  }
}

function SourceGroupDetailCell({ row }: { row: SourceGroupDto }) {
  const { t: tCountry, i18n } = useTranslation('countriesByIsoAlpha2')

  if (row.type === 'EXTERNAL_COUNTRY_KNOWN' && row.country) {
    const countryCode = row.country.toLowerCase()
    return (
      <span className="text-muted-foreground">
        {i18n.exists(countryCode, { ns: 'countriesByIsoAlpha2' })
          ? tCountry(countryCode)
          : row.country.toUpperCase()}
      </span>
    )
  }

  if (row.type === 'EXTERNAL_INSTALLATION_KNOWN' && row.supplierInstallation) {
    return (
      <span className="text-muted-foreground">
        {formatSourceGroupSupplierInstallationLabel({
          id: row.supplierInstallation.id,
          name: row.supplierInstallation.name,
          localName: null,
          country: null,
          supplierOrganizationId: row.supplierInstallation.supplierOrganization.id,
          supplierOrganizationName: row.supplierInstallation.supplierOrganization.name,
          supplierOrganizationLocalName:
            row.supplierInstallation.supplierOrganization.localName,
        })}
      </span>
    )
  }

  return <span className="text-muted-foreground">—</span>
}

export function InstallationPrecursorCnCodeSourceGroupsPage() {
  const { t } = useTranslation('installations')
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('installation', 'update')
  const { installationId, precursorCnCodeId } = useParams<{
    installationId: string
    precursorCnCodeId: string
  }>()

  const precursorListQuery = useInstallationPrecursorCnCodesQuery()
  const listQuery = useInstallationSourceGroupsQuery()
  const supplierOptionsQuery = useOrganizationSupplierInstallationsQuery()
  const mutations = useInstallationSourceGroupMutations()
  const deleteConfirm = useInstallationDeleteConfirm()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [createForm, setCreateForm] = useState<SourceGroupCreateFormState>(
    defaultSourceGroupCreateFormState
  )
  const [editForm, setEditForm] = useState<SourceGroupFormState>(defaultSourceGroupFormState)
  const [editingSourceGroupId, setEditingSourceGroupId] = useState<string | null>(null)
  const [editingSourceGroup, setEditingSourceGroup] = useState<SourceGroupDto | null>(null)
  const [createValidationAttempted, setCreateValidationAttempted] = useState(false)
  const [editValidationAttempted, setEditValidationAttempted] = useState(false)

  const precursorCnCode = useMemo(() => {
    return precursorListQuery.data?.find((row) => row.id === precursorCnCodeId)?.cnCode
  }, [precursorCnCodeId, precursorListQuery.data])

  const rows = listQuery.data
  const supplierInstallationOptions = useMemo(
    () => supplierOptionsQuery.data ?? [],
    [supplierOptionsQuery.data]
  )

  const selectedCreateSupplierInstallation = useMemo(() => {
    return supplierInstallationOptions.find(
      (option) => option.id === createForm.supplierInstallationId
    )
  }, [createForm.supplierInstallationId, supplierInstallationOptions])

  const createEmissionReportsQuery = useSupplierInstallationEmissionReportsQuery(
    selectedCreateSupplierInstallation?.supplierOrganizationId,
    createForm.type === 'EXTERNAL_INSTALLATION_KNOWN'
      ? createForm.supplierInstallationId
      : undefined
  )

  const createEmissionReportOptions = useMemo(
    () =>
      filterSourceGroupEmissionReportsForCnCode(
        createEmissionReportsQuery.data ?? [],
        precursorCnCode
      ),
    [createEmissionReportsQuery.data, precursorCnCode]
  )
  const createInstallationHasEmissionReports = createEmissionReportOptions.length > 0
  const {
    awaitingEmissionReports: createAwaitingEmissionReports,
    emissionReportsLoaded: createEmissionReportsLoaded,
  } = getSourceGroupEmissionReportsState(
    createForm.type,
    createForm.supplierInstallationId,
    selectedCreateSupplierInstallation?.supplierOrganizationId,
    createEmissionReportsQuery
  )

  const selectedEditSupplierInstallation = useMemo(() => {
    return supplierInstallationOptions.find(
      (option) => option.id === editForm.supplierInstallationId
    )
  }, [editForm.supplierInstallationId, supplierInstallationOptions])

  const editEmissionReportsQuery = useSupplierInstallationEmissionReportsQuery(
    selectedEditSupplierInstallation?.supplierOrganizationId,
    editForm.type === 'EXTERNAL_INSTALLATION_KNOWN'
      ? editForm.supplierInstallationId
      : undefined
  )

  const editEmissionReportOptions = useMemo(
    () =>
      filterSourceGroupEmissionReportsForCnCode(
        editEmissionReportsQuery.data ?? [],
        precursorCnCode
      ),
    [editEmissionReportsQuery.data, precursorCnCode]
  )
  const editInstallationHasEmissionReports = editEmissionReportOptions.length > 0
  const {
    awaitingEmissionReports: editAwaitingEmissionReports,
    emissionReportsLoaded: editEmissionReportsLoaded,
  } = getSourceGroupEmissionReportsState(
    editForm.type,
    editForm.supplierInstallationId,
    selectedEditSupplierInstallation?.supplierOrganizationId,
    editEmissionReportsQuery
  )

  useEffect(() => {
    if (!editOpen || !editingSourceGroup || editForm.supplierInstallationEmissionReportId) {
      return
    }

    if (!editEmissionReportsLoaded) {
      return
    }

    const inferredEmissionReportId = inferSourceGroupEmissionReportId(
      editingSourceGroup.specificEmbeddedEmissions,
      editEmissionReportOptions
    )

    if (!inferredEmissionReportId) {
      return
    }

    setEditForm((current) => ({
      ...current,
      supplierInstallationEmissionReportId: inferredEmissionReportId,
    }))
  }, [
    editOpen,
    editingSourceGroup,
    editEmissionReportsLoaded,
    editEmissionReportOptions,
    editForm.supplierInstallationEmissionReportId,
  ])

  useEffect(() => {
    setCreateForm((current) => {
      const selectedId = current.supplierInstallationEmissionReportId.trim()
      if (
        !selectedId ||
        createEmissionReportOptions.some((report) => report.id === selectedId)
      ) {
        return current
      }

      return { ...current, supplierInstallationEmissionReportId: '' }
    })
  }, [createEmissionReportOptions])

  useEffect(() => {
    setEditForm((current) => {
      const selectedId = current.supplierInstallationEmissionReportId.trim()
      if (
        !selectedId ||
        editEmissionReportOptions.some((report) => report.id === selectedId)
      ) {
        return current
      }

      return { ...current, supplierInstallationEmissionReportId: '' }
    })
  }, [editEmissionReportOptions])

  const precursorListPath =
    installationId &&
    `/dashboard/${installationId}/installation/configuration/precursor-cn-codes`

  const resetCreateForm = () => {
    setCreateForm(defaultSourceGroupCreateFormState)
    setCreateValidationAttempted(false)
  }

  const resetEditForm = () => {
    setEditingSourceGroupId(null)
    setEditingSourceGroup(null)
    setEditForm(defaultSourceGroupFormState)
    setEditValidationAttempted(false)
  }

  const submitCreate = async () => {
    setCreateValidationAttempted(true)
    if (createAwaitingEmissionReports) {
      return
    }
    if (
      !isSourceGroupCreateFormValid(createForm, {
        installationHasEmissionReports: createInstallationHasEmissionReports,
      })
    ) {
      return
    }
    try {
      await mutations.addSourceGroup.mutateAsync(toSourceGroupCreatePayload(createForm))
      resetCreateForm()
      setCreateOpen(false)
      toast.success(t('configuration.sourceGroups.notifications.createSuccess'))
    } catch {
      toast.error(t('configuration.sourceGroups.notifications.createError'))
    }
  }

  const submitEdit = async () => {
    setEditValidationAttempted(true)
    if (editAwaitingEmissionReports) {
      return
    }
    if (
      !editingSourceGroupId ||
      !isSourceGroupFormValid(editForm, {
        installationHasEmissionReports: editInstallationHasEmissionReports,
      })
    ) {
      return
    }
    try {
      await mutations.patchSourceGroup.mutateAsync({
        sourceGroupId: editingSourceGroupId,
        payload: toSourceGroupPatchPayload(editForm),
      })
      resetEditForm()
      setEditOpen(false)
      toast.success(t('configuration.sourceGroups.notifications.updateSuccess'))
    } catch {
      toast.error(t('configuration.sourceGroups.notifications.updateError'))
    }
  }

  const openEdit = (row: SourceGroupDto) => {
    setEditingSourceGroupId(row.id)
    setEditingSourceGroup(row)
    setEditForm(sourceGroupFormStateFromDto(row))
    setEditValidationAttempted(false)
    setEditOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.pendingDelete) {
      return
    }
    try {
      await mutations.removeSourceGroup.mutateAsync(deleteConfirm.pendingDelete.id)
      deleteConfirm.clearPendingDelete()
      toast.success(t('configuration.sourceGroups.notifications.deleteSuccess'))
    } catch {
      toast.error(t('configuration.sourceGroups.notifications.deleteError'))
    }
  }

  const createCountryInvalid =
    createValidationAttempted &&
    createForm.type === 'EXTERNAL_COUNTRY_KNOWN' &&
    createForm.country.trim().length !== 2
  const createSupplierInstallationInvalid =
    createValidationAttempted &&
    createForm.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    !createForm.supplierInstallationId.trim()
  const editCountryInvalid =
    editValidationAttempted &&
    editForm.type === 'EXTERNAL_COUNTRY_KNOWN' &&
    editForm.country.trim().length !== 2
  const editSupplierInstallationInvalid =
    editValidationAttempted &&
    editForm.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    !editForm.supplierInstallationId.trim()
  const createEmissionReportInvalid =
    createValidationAttempted &&
    createForm.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    createInstallationHasEmissionReports &&
    !createForm.supplierInstallationEmissionReportId.trim()
  const editEmissionReportInvalid =
    editValidationAttempted &&
    editForm.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    editInstallationHasEmissionReports &&
    !editForm.supplierInstallationEmissionReportId.trim()

  if (
    listQuery.isLoading ||
    precursorListQuery.isLoading ||
    supplierOptionsQuery.isLoading
  ) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (rows === undefined || precursorCnCode === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('configuration.loadError')}</p>
        {precursorListPath && (
          <Button type="button" variant="outline" asChild>
            <Link to={precursorListPath}>{t('configuration.sourceGroups.backToPrecursors')}</Link>
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex flex-col gap-4">
        {precursorListPath && (
          <Button type="button" variant="ghost" className="w-fit px-0" asChild>
            <Link to={precursorListPath}>
              <ArrowLeft className="size-4" />
              {t('configuration.sourceGroups.backToPrecursors')}
            </Link>
          </Button>
        )}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('configuration.sourceGroups.title', { cnCode: precursorCnCode })}
            </h1>
            <p className="text-muted-foreground">
              {t('configuration.sourceGroups.description')}
            </p>
          </div>
          {canEdit && rows.length > 0 && (
            <Button
              type="button"
              onClick={() => {
                resetCreateForm()
                setCreateOpen(true)
              }}
            >
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
          <Inbox className="size-10 text-muted-foreground/50" />
          <div className="space-y-1 text-center">
            <h3 className="text-lg font-semibold">
              {t('configuration.sourceGroups.emptyTitle')}
            </h3>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('configuration.sourceGroups.emptyDescription')}
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              onClick={() => {
                resetCreateForm()
                setCreateOpen(true)
              }}
            >
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="group/row">
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[20%] whitespace-normal'
                  )}
                >
                  {t('configuration.sourceGroups.table.type')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[22%] whitespace-normal'
                  )}
                >
                  {t('configuration.sourceGroups.table.description')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[14%] whitespace-normal'
                  )}
                >
                  {t('configuration.sourceGroups.table.productionRoute')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[18%] whitespace-normal'
                  )}
                >
                  {t('configuration.sourceGroups.table.specificEmbeddedEmissions')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[24%] whitespace-normal'
                  )}
                >
                  {t('configuration.sourceGroups.table.details')}
                </TableHead>
                {canEdit && (
                  <TableHead
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[120px] min-w-0 max-w-[120px] text-end'
                    )}
                  >
                    {t('configuration.table.actions')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[20%] whitespace-normal'
                    )}
                  >
                    {t(`configuration.sourceGroups.typeLabels.${row.type}`)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[22%] whitespace-normal'
                    )}
                  >
                    {row.description?.trim() ? row.description : '—'}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[14%] whitespace-normal'
                    )}
                  >
                    {row.productionRoute
                      ? `${row.productionRoute} — ${t(`productionRoutes.${row.productionRoute}`)}`
                      : '—'}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[18%] whitespace-normal tabular-nums'
                    )}
                  >
                    {row.specificEmbeddedEmissions ?? '—'}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[24%] whitespace-normal'
                    )}
                  >
                    <SourceGroupDetailCell row={row} />
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'w-[120px] min-w-0 max-w-[120px] text-end'
                      )}
                    >
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={t('configuration.a11y.editSourceGroup')}
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={t('configuration.a11y.deleteSourceGroup')}
                          onClick={() =>
                            deleteConfirm.requestDelete({
                              id: row.id,
                              label:
                                row.description?.trim() ||
                                t(`configuration.sourceGroups.typeLabels.${row.type}`),
                            })
                          }
                          disabled={mutations.removeSourceGroup.isPending}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent
          className="max-h-[min(90vh,48rem)] overflow-y-auto sm:max-w-lg"
          onInteractOutside={preventDialogDismissForCombobox}
          onPointerDownOutside={preventDialogDismissForCombobox}
        >
          <DialogPortalContainer>
            <DialogHeader>
              <DialogTitle>{t('configuration.sourceGroups.dialog.add')}</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <SourceGroupFormFields
                namePrefix="source-group-create"
                form={createForm}
                onChange={setCreateForm}
                supplierInstallationOptions={supplierInstallationOptions}
                countryInvalid={createCountryInvalid}
                supplierInstallationInvalid={createSupplierInstallationInvalid}
                emissionReportOptions={createEmissionReportOptions}
                emissionReportInvalid={createEmissionReportInvalid}
                emissionReportsLoaded={createEmissionReportsLoaded}
                selectedSupplierInstallationCountry={
                  selectedCreateSupplierInstallation?.country ?? null
                }
              />
            </div>
            <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={
                mutations.addSourceGroup.isPending || createAwaitingEmissionReports
              }
              onClick={submitCreate}
            >
              {t('configuration.save')}
            </Button>
            </DialogFooter>
          </DialogPortalContainer>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open)
          if (!open) {
            resetEditForm()
          }
        }}
      >
        <DialogContent
          className="max-h-[min(90vh,48rem)] overflow-y-auto sm:max-w-lg"
          onInteractOutside={preventDialogDismissForCombobox}
          onPointerDownOutside={preventDialogDismissForCombobox}
        >
          <DialogPortalContainer>
            <DialogHeader>
              <DialogTitle>{t('configuration.sourceGroups.dialog.edit')}</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <SourceGroupFormFields
                namePrefix="source-group-edit"
                form={editForm}
                onChange={setEditForm}
                supplierInstallationOptions={supplierInstallationOptions}
                countryInvalid={editCountryInvalid}
                supplierInstallationInvalid={editSupplierInstallationInvalid}
                emissionReportOptions={editEmissionReportOptions}
                emissionReportsLoaded={editEmissionReportsLoaded}
                emissionReportInvalid={editEmissionReportInvalid}
                selectedSupplierInstallationCountry={
                  selectedEditSupplierInstallation?.country ?? null
                }
              />
            </div>
            <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditOpen(false)
                resetEditForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={
                mutations.patchSourceGroup.isPending || editAwaitingEmissionReports
              }
              onClick={submitEdit}
            >
              {t('configuration.save')}
            </Button>
            </DialogFooter>
          </DialogPortalContainer>
        </DialogContent>
      </Dialog>

      <InstallationDeleteConfirmDialog
        pendingDelete={deleteConfirm.pendingDelete}
        open={deleteConfirm.isConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteConfirm.clearPendingDelete()
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.removeSourceGroup.isPending}
      />
    </div>
  )
}
